//https://dev.to/localeai/architecting-http-clients-in-vue-js-applications-for-effective-network-communication-1eec
//https://axios-http.com/docs/interceptors

import { useRegionStore } from "./region";
import { useUserStore } from "./user";
import { Notify } from "quasar";
import {
  isCanceledRequest,
  isPageUnloading,
  markApiErrorHandled,
  markApiErrorReported,
  reportApiErrorToDatadog,
} from "../services/apiErrorReporting";
import { router as routerInstance } from "../router";

// Re-entrancy latch for the 401 logout-and-redirect sequence.
//
// Several requests can be in flight at once, and when a session expires they
// all 401 together. Only the FIRST 401 should run the full sequence (one
// toast, one navigation, one session clear); the rest must reject quietly so
// we don't stack duplicate toasts, fire redundant navigations, or clear the
// session more than once. The latch is released once the sequence finishes
// (after the navigation settles and state is cleared), so a later, legitimate
// session expiry runs the sequence again from scratch.
let logoutRedirectInProgress = false;

// Central 401 handler. THE contract: every 401 reroutes to /login carrying a
// redirect_to back to the originally-requested page, and — critically — we
// navigate FIRST and only clear session state AFTER the navigation settles.
//
// The old code cleared session state synchronously *before* the async router
// navigation. The outgoing page was still mounted for a frame, so it
// re-rendered against a null user — the null-render window that ~10 defensive
// null-guard MRs were each papering over. vue-router's push() promise resolves
// only after the navigation is confirmed and the <router-view> has swapped the
// outgoing component out, so clearing in .finally() guarantees the old page is
// already unmounted by the time user.value goes null.
//
// `setOrganization` / `clearSession` are injected so both the axios
// interceptor and region.js's manual 401 site share one implementation and one
// latch. Returns a rejected promise so callers can `return handle401(...)`.
export function handle401(error, { setOrganization, clearSession }) {
  // Already on the login page: there's nothing to redirect to, and Login.vue
  // manages its own state — just propagate the rejection. Deliberately does
  // NOT engage the latch, toast, navigate, or clear.
  if (window.location.pathname === '/login') {
    return Promise.reject(error);
  }

  // From here on the visitor gets the standard expiry experience — either from
  // this call or, under the latch below, from the concurrent 401 that is
  // already running it. Either way a toast has been (or is being) shown and a
  // redirect is under way, so mark the error handled: view-level catch blocks
  // read this to suppress their own console.error/notify, which would otherwise
  // mint a duplicate Datadog issue via RUM's console instrumentation and stack
  // a second toast on top of ours (GitLab #895).
  //
  // Stamped for the latched case too, and that is the point rather than an
  // oversight: a dashboard fires several requests at once, so an expiring
  // session rejects all of them together. Only the first runs the sequence; if
  // the rest read as unhandled, every one of them would log and notify — the
  // exact duplication this exists to remove.
  markApiErrorHandled(error);

  // A logout/redirect from an earlier concurrent 401 is already underway.
  if (logoutRedirectInProgress) {
    return Promise.reject(error);
  }
  logoutRedirectInProgress = true;

  Notify.create({
    color: 'negative',
    message: error?.response?.data?.message,
    icon: 'report_problem'
  });

  // Build the login target the same way the router's own auth guards do
  // (router/index.js: next({ path: '/login', query: { redirect_to: to.fullPath } })),
  // so query encoding stays consistent and fullPaths carrying their own query
  // string survive the round-trip.
  const currentPath = routerInstance.currentRoute.value?.fullPath;
  const loginTarget = currentPath
    ? { path: '/login', query: { redirect_to: currentPath } }
    : { path: '/login' };

  // Promise.resolve() tolerates a push() that returns undefined (e.g. mocked in
  // tests); against the real router it awaits the confirmed navigation.
  Promise.resolve(routerInstance.push(loginTarget)).finally(() => {
    setOrganization(null);
    clearSession();
    localStorage.removeItem('logged_in');
    logoutRedirectInProgress = false;
  });

  return Promise.reject(error);
}

// Per-request opt-out from the toast, for a failure the CALLER owns.
//
// A request may set `silent_reasons: ['...']` in its axios config, listing the
// machine-readable `reason` markers the backend sends with answers this caller
// handles itself. /license/assign's `license_pending` is the first: the student
// stays on StudentPayment.vue with the assigning overlay up while the worker
// finishes (#634), and a toast telling them to "try again shortly" over a page
// that IS trying again is noise on top of a working flow.
//
// Only the notification and the Datadog report are skipped - a reason the
// caller asked to be quiet about is an expected outcome of that call, not a
// fault worth an Error Tracking issue. The promise still rejects, so the caller
// sees the failure and decides what to do; a reason NOT in the list, on the
// same request, toasts exactly as before.
function isSilencedReason(error) {
  const silenced = error?.config?.silent_reasons;
  const reason = error?.response?.data?.reason;
  return Array.isArray(silenced) && !!reason && silenced.includes(reason);
}

const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

function isWriteRequest(config) {
  return WRITE_METHODS.includes((config?.method || '').toLowerCase());
}

// Owner design (2026-09-29): one small map of fallback copy, so the wording
// can be edited in a single place. A fallback is used ONLY when the server's
// response carries no `message` of its own — a server message always wins.
//
// MAY_HAVE_COMPLETED backs two situations that read the same to a visitor: a
// write whose response never arrived at all (opted in per-request via the
// `mayHaveCompleted` config flag — see the response-less branch below) and a
// write that got a 502/504 (always, no flag needed — see the switch below).
// Both mean "we don't know if the server actually did it", which is a
// materially different, and more urgent, message than a failed read.
const FALLBACK_MESSAGES = {
  400: "We couldn't process that request. Check what you entered and try again.",
  403: "You don't have permission to do that.",
  404: "We couldn't find that. It may have been deleted or moved.",
  409: 'This conflicts with a change made somewhere else. Reload to see the latest version before trying again.',
  413: 'This is too large to save. Remove pasted images or shorten the text and try again.',
  422: "Some of the information entered isn't valid. Check the form and try again.",
  429: 'Too many requests. Wait a moment and try again.',
  READ_TIMEOUT: 'The server took too long to respond. Please try again in a moment.',
  MAY_HAVE_COMPLETED: 'We lost the connection before we heard back, so this may have been saved. Check before trying again.',
  WRITE_500: 'Something went wrong and it has been reported to the development team.<br>Your change may not have been saved. Try again, and if it keeps happening email <a href="mailto:support@chartflow.io" target="_blank" class="text-white text-bold" style="color: #ffffff !important;">support@chartflow.io</a>',
};

// `action` (per-request axios config, e.g. `{ action: 'save the grade for
// Jane Doe / Robert Gomez' }`) lets a caller who already knows what the user
// was trying to do ask the toast to lead with that, instead of keeping its
// own separate toast alongside this one (GradingReportPage.vue's
// saveGradeSilent used to). Applied uniformly on top of whichever message
// this interceptor already chose — server-sent or a FALLBACK_MESSAGES entry
// — so it needs no per-branch plumbing of its own.
function withAction(error, message) {
  const action = error?.config?.action;
  return action ? `Couldn't ${action}. ${message}` : message;
}

// Some endpoints (passkey_login, passkey_registration, become_user,
// handle_users_data, lti_launch, OrchestrateXR's 422s, ...) answer a 4xx with
// an `error` or `errors` key instead of `message` — a shape their own caller
// already reads and handles itself (a silent retry, an inline form error,
// its own UI). Before the fallback map existed, `data.message` was simply
// undefined for these, so error_intercept silently skipped the toast; the
// fallback map must preserve that silence rather than papering every one of
// them with a generic toast on top of what the caller already shows. A
// `message` key still always wins and always toasts — this only changes
// what happens when there is NO message: fall back to the generic copy only
// when the body carries no `error`/`errors` of its own to own instead.
function hasCallerOwnedErrorBody(data) {
  return !!(data && (
    Object.prototype.hasOwnProperty.call(data, 'error') ||
    Object.prototype.hasOwnProperty.call(data, 'errors')
  ));
}

// Resolves what error_intercept should say for a response with `data`, given
// this status's fallback copy — or null when nothing should be said at all
// (see hasCallerOwnedErrorBody).
function resolveStatusMessage(data, fallback) {
  if (data?.message) return data.message;
  if (hasCallerOwnedErrorBody(data)) return null;
  return fallback;
}

export function useRestapi() {
  const regionStore = useRegionStore();
  const userStore = useUserStore();

  function intercept(response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  }

  function error_intercept(error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    // all the other error responses
    console.log("Error Inteceptor: error", error);
    // Stamp every failure this interceptor sees, before any of the branching
    // below decides whether to report it. boot/rum.js keys off the stamp to
    // drop RUM's own context-free captures of the same failure, so the mark has
    // to cover the ones we deliberately stay quiet about (canceled requests,
    // expected login_options 404s) too — suppressing our enriched report
    // accomplishes nothing if RUM's duplicate still mints an issue.
    markApiErrorReported(error);
    // A caller-initiated cancellation (e.g. LoginForm.vue aborting the
    // conditional passkey ceremony on unmount) isn't a failure the visitor
    // needs to hear about — reject quietly, same as reportApiErrorToDatadog's
    // handling below.
    if (isCanceledRequest(error)) {
      return Promise.reject(error);
    }
    // The caller declared this specific `reason` its own to handle (see
    // isSilencedReason): stay quiet and let the rejection reach it.
    if (isSilencedReason(error)) {
      markApiErrorHandled(error);
      return Promise.reject(error);
    }
    if (error && error.response) {
      console.log("Error status:", error.response.status, typeof error.response.status);
      switch (error.response.status) {
        case 400: {
          const message = resolveStatusMessage(error.response.data, FALLBACK_MESSAGES[400]);
          if (message) {
            Notify.create({
              color: 'negative',
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 401: // authentication error, logout the user
          console.log("Error Inteceptor: 401 error");
          // Report to Datadog first (see reportApiErrorToDatadog), then hand
          // off to the central navigate-then-clear handler. handle401 owns the
          // toast, the redirect, the re-entrancy latch, and the deferred state
          // clear, so both this interceptor and region.js's manual 401 site
          // behave identically.
          reportApiErrorToDatadog(error);
          return handle401(error, {
            setOrganization: regionStore.setOrganization,
            clearSession: () => userStore.sessionStore.clear_session()
          });
        case 403: {
          const message = resolveStatusMessage(error.response.data, FALLBACK_MESSAGES[403]);
          if (message) {
            Notify.create({
              color: 'negative',
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 404: {
          const message = resolveStatusMessage(error.response.data, FALLBACK_MESSAGES[404]);
          if (message) {
            Notify.create({
              color: 'negative',
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 409: {
          const message = resolveStatusMessage(error.response.data, FALLBACK_MESSAGES[409]);
          if (message) {
            Notify.create({
              color: 'negative',
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 413: {
          // A save whose payload is too big for the database (the API's
          // ItemTooLargeError) or for the gateway. Nothing was saved and the
          // user can fix it themselves, so it always gets a message.
          const message = resolveStatusMessage(error.response.data, FALLBACK_MESSAGES[413]);
          if (message) {
            Notify.create({
              color: 'negative',
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 422: {
          const message = resolveStatusMessage(error.response.data, FALLBACK_MESSAGES[422]);
          if (message) {
            Notify.create({
              color: 'negative',
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 429: {
          const message = resolveStatusMessage(error.response.data, FALLBACK_MESSAGES[429]);
          if (message) {
            Notify.create({
              color: 'negative',
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 500: {
          console.log("Error Inteceptor: 500 error - MATCHED");
          // A 500 on a write means the visitor doesn't know whether their
          // change landed — that's worse than a failed read, which changed
          // nothing — so it gets its own wording (owner design, 2026-09-29).
          const isWrite = isWriteRequest(error.config);
          const fallback = isWrite
            ? FALLBACK_MESSAGES.WRITE_500
            : 'There was an error that was automatically reported to the development team.<br>Please refresh your page and try again.<br>If this continues to be an issue, please email <a href="mailto:support@chartflow.io" target="_blank" class="text-white text-bold" style="color: #ffffff !important;">support@chartflow.io</a>';
          const message = resolveStatusMessage(error.response.data, fallback);
          if (message) {
            Notify.create({
              color: 'negative',
              html: true,
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 502:
        case 504: {
          // Same read-vs-write split as the response-less branch below: a
          // write that timed out at the gateway may or may not have reached
          // the server, so it ALWAYS gets the may-have-completed wording —
          // unlike the response-less case, this isn't opt-in per-request,
          // because a 502/504 response (unlike a plain network error) means
          // the request definitely got as far as chartflow's own gateway.
          const isWrite = isWriteRequest(error.config);
          const fallback = isWrite ? FALLBACK_MESSAGES.MAY_HAVE_COMPLETED : FALLBACK_MESSAGES.READ_TIMEOUT;
          const message = resolveStatusMessage(error.response.data, fallback);
          if (message) {
            Notify.create({
              color: 'negative',
              message: withAction(error, message),
              icon: 'report_problem'
            });
          }
          break;
        }
        case 503:
          // Maintenance mode - redirect to maintenance page
          console.log("Error Interceptor: 503 maintenance mode");
          if (error.response.data && error.response.data.maintenance_mode) {
            // Store maintenance message for the maintenance page
            sessionStorage.setItem('maintenance_message', error.response.data.message || 'System is currently under maintenance. Please try again later.');
            // Redirect to maintenance page
            routerInstance.push('/maintenance');
          }
          break;
        default:
          console.log("Error Interceptor: Unhandled status", error.response.status);
      }
    } else if (isPageUnloading()) {
      // Browser is tearing the page down (navigation, reload, tab close) —
      // every in-flight request gets aborted and axios surfaces that as a
      // plain response-less "Network Error", identical in shape to a real
      // outage. Same rule apiErrorReporting.js's isUnexpectedUninterceptedFailure
      // applies for the auth surface (see its pageUnloading comment); this is
      // the equivalent for every other API call in the app, which previously
      // had no such guard (Datadog issue 6af3acdc: GET .../flowsheet/mar/visit/...
      // firing on ordinary chart navigation).
      return Promise.reject(error);
    } else {
      // Network error or no response
      console.log("Error Interceptor: Network error or no response");
      // A failed write needs the opposite advice from a failed read, so the
      // message is chosen by method rather than by endpoint. Response-less
      // failures span ~65 endpoints (auth, catalog, lookups, course progress,
      // charting), so anything endpoint-specific would cover a fraction of
      // them; the wording therefore has to hold for a dropped login as well as
      // a dropped chart save.
      //
      // Why "do not refresh" is the important half: a request that never
      // reached the server leaves the change in the page. The flowsheet
      // composable keeps it by not advancing `original_data` when the save
      // rejects (patient_data.js:253), so the next save resends the same diff
      // — but a refresh refetches and calls reset_data, replacing both the
      // data and the baseline with whatever the server has. The old blanket
      // "please refresh your page" therefore told a student mid-chart to do
      // the one thing that discards the work they were trying to save
      // (Datadog issue 31d7806e, POST /patient/{uid}/flowsheet/{uid}).
      const isWrite = isWriteRequest(error?.config);
      // `silent` is honored ONLY here, in the response-less branch — not in
      // the status switch above. It exists for a caller (StudentPayment.vue's
      // apply_payment_code, via ApplyPaymentCode's `silent: true`) that shows
      // its own code-specific message for exactly this shape ("we don't know
      // if the code was applied") and would otherwise double up with this
      // toast. Scoping the flag to the response-less branch means a genuine
      // 5xx on that same request still gets the ordinary global handling
      // above, unaffected — only the "no response at all" case goes quiet.
      if (error?.config?.silent) {
        markApiErrorHandled(error);
      } else {
        // `mayHaveCompleted` softens a write's wording from "nothing was
        // saved" to "this may have been saved" for callers who know the
        // request could plausibly have reached the server despite the
        // dropped response (e.g. addScene, checkout/purchase calls) — see
        // FALLBACK_MESSAGES.MAY_HAVE_COMPLETED. Unset, a response-less write
        // keeps its original, more confident wording.
        const mayHaveCompleted = isWrite && !!error?.config?.mayHaveCompleted;
        Notify.create({
          color: 'negative',
          html: true,
          message: withAction(error, mayHaveCompleted
            ? FALLBACK_MESSAGES.MAY_HAVE_COMPLETED
            : (isWrite
              ? 'The network connection dropped before this request reached the server, so nothing was saved.<br>Check your connection and try again — refreshing the page may discard unsaved work.<br>If this continues to be an issue, please email <a href="mailto:support@chartflow.io" target="_blank" class="text-white text-bold" style="color: #ffffff !important;">support@chartflow.io</a>'
              : 'There was an error that was automatically reported to the development team.<br>Please refresh your page and try again.<br>If this continues to be an issue, please email <a href="mailto:support@chartflow.io" target="_blank" class="text-white text-bold" style="color: #ffffff !important;">support@chartflow.io</a>')),
          icon: 'report_problem'
        });
      }
    }
    reportApiErrorToDatadog(error);
    return Promise.reject(error);
  }

  return {
    intercept,
    error_intercept,
  }
}
